import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import '../assets/css/Analytics.css';
import { extractErrorMessage } from '../Utils/ExtractMessages';
import {
  fetchAnalyticsDatasets,
  fetchAnalyticsSeries,
  fetchAnalyticsSummary,
} from './analyticsDataSource';
import AnalyticsSummaryCards from './AnalyticsSummaryCards';
import AnalyticsSeriesPanel from './AnalyticsSeriesPanel';
import { defaultDateRange, getDimensionValues, validateDateRange } from './analyticsUtils';

function Analytics() {
  const initialRange = defaultDateRange();

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetName, setSelectedDatasetName] = useState('');
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  const [datasetsError, setDatasetsError] = useState(null);

  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState(null);

  // The dimension option list is only rebuilt from an UNFILTERED response.
  // A filtered response contains just the selected dimension, so rebuilding
  // from it would collapse the dropdown and strand the user on one value.
  const [dimensionOptions, setDimensionOptions] = useState([]);

  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);
  const [dimension, setDimension] = useState('');
  const [rangeError, setRangeError] = useState(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await fetchAnalyticsSummary());
      setSummaryError(null);
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      const message = extractErrorMessage(error);
      toast.error(message);
      // An error is kept distinct from an empty result. "No figures yet" and
      // "we could not reach the server" must never look the same to the user.
      setSummary(null);
      setSummaryError(message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadDatasets = useCallback(async () => {
    setDatasetsLoading(true);
    try {
      const available = await fetchAnalyticsDatasets();
      setDatasets(available);
      setDatasetsError(null);
      if (available.length > 0) {
        setSelectedDatasetName((current) => current || available[0].datasetName);
      }
    } catch (error) {
      console.error('Error fetching analytics datasets:', error);
      const message = extractErrorMessage(error);
      toast.error(message);
      // Failing to list the datasets leaves the chart with nothing to draw, but
      // that is a failure and not an empty result. Without its own error state
      // the panel would fall through to "no data points", which tells the user
      // the platform has no data when in fact the request never succeeded.
      setDatasets([]);
      setDatasetsError(message);
    } finally {
      setDatasetsLoading(false);
    }
  }, []);

  const loadSeries = useCallback(async () => {
    if (!selectedDatasetName) {
      return;
    }

    const validationMessage = validateDateRange(fromDate, toDate);
    setRangeError(validationMessage);
    if (validationMessage) {
      return;
    }

    setSeriesLoading(true);
    try {
      const loaded = await fetchAnalyticsSeries(selectedDatasetName, {
        fromDate,
        toDate,
        dimension,
      });
      setSeries(loaded);
      setSeriesError(null);
      if (!dimension) {
        setDimensionOptions(getDimensionValues(loaded?.points));
      }
    } catch (error) {
      console.error('Error fetching analytics series:', error);
      const message = extractErrorMessage(error);
      toast.error(message);
      setSeries(null);
      setSeriesError(message);
    } finally {
      setSeriesLoading(false);
    }
  }, [selectedDatasetName, fromDate, toDate, dimension]);

  useEffect(() => {
    loadSummary();
    loadDatasets();
  }, [loadSummary, loadDatasets]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // The chart is pending while the dataset list is in flight, while a series
  // request is in flight, AND in the gap between the two - the moment where a
  // dataset has just been selected but its series request has not started yet.
  // Without that last case the panel drops out of its loading state for one
  // render and blinks through "no data points" on the way to its first chart,
  // which reads as a confirmed absence of data rather than as work in progress.
  const seriesPending =
    datasetsLoading ||
    seriesLoading ||
    (!!selectedDatasetName && !series && !seriesError && !rangeError);

  // Changing dataset drops any dimension filter - the dimension values belong
  // to the dataset that was previously selected.
  const handleSelectDataset = (datasetName) => {
    setSelectedDatasetName(datasetName);
    setDimension('');
    setDimensionOptions([]);
  };

  return (
    <div className="custom_form_wrapper analytics-wrapper">
      <div className="custom_form_header">
        <p className="form_head_title">Analytics</p>
      </div>

      <AnalyticsSummaryCards
        cards={summary?.cards || []}
        loading={summaryLoading}
        error={summaryError}
        asOfDate={summary?.asOfDate}
      />

      <AnalyticsSeriesPanel
        datasets={datasets}
        selectedDatasetName={selectedDatasetName}
        onSelectDataset={handleSelectDataset}
        series={series}
        loading={seriesPending}
        error={seriesError || datasetsError}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        dimension={dimension}
        dimensionOptions={dimensionOptions}
        onDimensionChange={setDimension}
        rangeError={rangeError}
      />
    </div>
  );
}

export default Analytics;
